import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowUp, Sparkles, X } from 'lucide-react';
import { useAttachmentContext } from './AttachmentContext';
import { useChatSession } from '@/hooks/useChatSession';
import { AttachmentChip } from './AttachmentChip';
import { ChatHistoryDropdown } from './ChatHistoryDropdown';
import { MarkdownText } from './MarkdownText';
import { useBreakpoint } from './useBreakpoint';

interface AnalyticsChatDrawerProps {
  teamId: number;
  days: number;
}

const DRAWER_WIDTH_PX = 420;
const OPEN_STATE_KEY = 'analyticsChatDrawerOpen';
const MOBILE_SHEET_MAX_VH = 80;

/**
 * Hook: measure the page-level vertical scrollbar width once and on resize.
 *
 * `position: fixed` is positioned relative to the layout viewport, which on
 * Chrome/Linux/Windows excludes the scrollbar gutter. Without compensation,
 * the drawer handle and panel sit ~6–17px (browser-dependent) from the true
 * right edge of the screen and look misaligned. Returning the measured gap
 * lets the drawer offset itself by that exact amount so it visually hugs
 * the right edge regardless of the platform's scrollbar conventions.
 */
function usePageScrollbarWidth(): number {
  const [w, setW] = useState(0);
  useEffect(() => {
    const measure = () =>
      setW(Math.max(0, window.innerWidth - document.documentElement.clientWidth));
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  return w;
}

/**
 * Hook: auto-grow a textarea to fit its content.
 *
 * Intended pairing: `rows={1}` (so the empty state collapses to a single
 * line and the placeholder stays vertically centered) plus a Tailwind
 * `max-h-*` cap so very long drafts stop growing and overflow-scroll
 * instead. Without this hook, `rows={1}` would clip multi-line drafts
 * to a single visible row with internal scrolling — a regression vs.
 * the older `rows={2}` static height.
 *
 * Returns a callback ref so resizing also fires on (re)mount. The
 * drawer can unmount the textarea while keeping the parent's `draft`
 * state alive (e.g. close → reopen with a multi-line draft already in
 * state); a plain `useEffect([value])` would not fire on remount when
 * the value hasn't changed, leaving the new textarea stuck at 1 row.
 */
function useAutoGrowTextarea(value: string) {
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const resize = useCallback((ta: HTMLTextAreaElement | null) => {
    if (!ta) return;
    // Reset to `auto` first so shrink-after-delete works; then read
    // the natural scrollHeight at single-line baseline and pin to it.
    ta.style.height = 'auto';
    ta.style.height = `${ta.scrollHeight}px`;
  }, []);
  const setRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      taRef.current = node;
      resize(node);
    },
    [resize]
  );
  // Resize on value change; covers typing + setDraft('') after send.
  useEffect(() => {
    resize(taRef.current);
  }, [value, resize]);
  return setRef;
}

export const AnalyticsChatDrawer: React.FC<AnalyticsChatDrawerProps> = (props) => {
  const bp = useBreakpoint();
  return bp === 'mobile' ? <MobileDrawer {...props} /> : <DesktopDrawer {...props} />;
};

const DesktopDrawer: React.FC<AnalyticsChatDrawerProps> = ({ teamId, days }) => {
  const { drawerOpen, setDrawerOpen, staged, unstageAttachment, clearStaged } = useAttachmentContext();
  const { sessionId, messages, isSending, send, startNewSession, switchToSession } = useChatSession({ teamId, days });
  const [draft, setDraft] = useState('');
  const [unread, setUnread] = useState(false);
  const lastSeenRef = useRef<number>(0);
  const hydratedRef = useRef(false);
  const scrollbarWidth = usePageScrollbarWidth();
  const draftRef = useAutoGrowTextarea(draft);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll the message list to the bottom whenever the conversation
  // grows or a response is being generated. Mirrors the existing behavior
  // in CoachChat so the drawer never leaves new content off-screen.
  useEffect(() => {
    if (!drawerOpen) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length, isSending, drawerOpen]);

  useEffect(() => {
    if (drawerOpen) {
      setUnread(false);
      lastSeenRef.current = messages.length;
      hydratedRef.current = true;
      return;
    }
    // First render with drawer closed: sync baseline to whatever history has
    // already been hydrated (prevents a false-positive unread dot for messages
    // the coach has already seen from the /chat tab).
    if (!hydratedRef.current) {
      lastSeenRef.current = messages.length;
      hydratedRef.current = true;
      return;
    }
    // Drawer closed: if new messages arrived, mark unread.
    if (messages.length > lastSeenRef.current) {
      setUnread(true);
    }
  }, [messages.length, drawerOpen]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || isSending) return;
    setDraft('');
    const toSend = [...staged];
    clearStaged();
    await send(text, toSend);
  };

  // Persist open state across reloads. `localStorageHydratedRef` gates the
  // write effect so the initial render's default `drawerOpen=false` doesn't
  // clobber a previously-stored `'true'` before the read effect has had a
  // chance to restore it. Without this, a tab that crashes in the tiny
  // window between the first write and the corrective re-render would lose
  // the user's preference.
  const localStorageHydratedRef = useRef(false);
  useEffect(() => {
    const stored = localStorage.getItem(OPEN_STATE_KEY);
    if (stored === 'true') setDrawerOpen(true);
    localStorageHydratedRef.current = true;
  }, [setDrawerOpen]);

  useEffect(() => {
    if (!localStorageHydratedRef.current) return;
    localStorage.setItem(OPEN_STATE_KEY, String(drawerOpen));
  }, [drawerOpen]);

  // ESC closes the drawer.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen, setDrawerOpen]);

  return (
    <>
      {/* Tab handle — always rendered, fixed to right edge, vertically centered.
          When CLOSED the handle sits at right:0 so it stays fully visible
          (a negative offset would slide ~17px under the OS scrollbar on
          Windows/Linux classic-scrollbar setups, hiding most of the tab).
          When OPEN the panel itself extends -scrollbarWidth past the
          layout viewport to hug the visual right edge, so the handle
          shifts to the panel's left edge: `DRAWER_WIDTH_PX - scrollbarWidth`. */}
      <button
        type="button"
        onClick={() => setDrawerOpen(!drawerOpen)}
        aria-label={drawerOpen ? 'Close AI chat' : 'Open AI chat'}
        className="fixed z-40 flex items-center justify-center bg-white text-neutral-900 border border-neutral-300 border-r-0 hover:bg-neutral-100 dark:bg-neutral-900 dark:text-neutral-200 dark:border-white/20 dark:hover:bg-neutral-800 transition-colors"
        style={{
          top: '50%',
          right: drawerOpen ? `${DRAWER_WIDTH_PX - scrollbarWidth}px` : 0,
          transform: 'translateY(-50%)',
          width: 28,
          height: 110,
        }}
      >
        <span
          className="font-medium tracking-widest text-xs"
          style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
        >
          ASK AI
        </span>
        {unread && !drawerOpen && (
          <span
            className="absolute top-2 right-2 bg-red-400"
            style={{ width: 8, height: 8 }}
            aria-label="Unread messages"
          />
        )}
      </button>

      {/* Drawer panel. Right offset matches the handle so the panel hugs
          the visual right edge of the viewport, not the layout viewport
          (which excludes the scrollbar gutter). */}
      {drawerOpen && (
        <aside
          className="fixed top-0 bottom-0 bg-white border-l border-neutral-200 dark:bg-neutral-900 dark:border-white/15 z-30 flex flex-col"
          style={{ width: DRAWER_WIDTH_PX, right: -scrollbarWidth }}
          aria-label="AI chat drawer"
        >
          <header className="flex items-center justify-between border-b border-neutral-200 dark:border-white/10 px-4 py-3">
            <div className="text-sm font-medium text-neutral-900 dark:text-neutral-200 truncate">
              {sessionId ? 'Chat' : 'New chat'}
            </div>
            <div className="flex items-center gap-2">
              <ChatHistoryDropdown
                teamId={teamId}
                currentSessionId={sessionId}
                onSelect={switchToSession}
              />
              <button
                type="button"
                onClick={startNewSession}
                className="text-xs text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-200 px-2 py-1"
                aria-label="Start new chat"
              >
                + New chat
              </button>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-200 p-1"
                aria-label="Close drawer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </header>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
            {messages.length === 0 && !isSending && (
              <div className="text-neutral-600 dark:text-neutral-400 text-sm">
                <p>Ask anything about your team's analytics.</p>
                <p className="mt-2 text-neutral-500">
                  Click a chart while this drawer is open to attach it as context.
                </p>
              </div>
            )}
            {messages.map(m => (
              <div key={m.id} className="space-y-2">
                {m.role === 'user' ? (
                  <div className="bg-neutral-100 border border-neutral-200 dark:bg-white/5 dark:border-white/10 px-3 py-2">
                    <div className="text-[11px] text-neutral-500 dark:text-neutral-400 mb-1">You</div>
                    <div className="text-sm text-neutral-900 dark:text-neutral-100 whitespace-pre-wrap">{m.content}</div>
                    {m.attachments && m.attachments.length > 0 && (
                      <div className="mt-2 space-y-1.5">
                        {m.attachments.map(a => (
                          <AttachmentChip key={a.chart_id} attachment={a} />
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <MarkdownText compact>{m.content}</MarkdownText>
                )}
              </div>
            ))}
            {isSending && (
              <div className="text-sm text-neutral-500 italic">Thinking…</div>
            )}
            {/* Scroll anchor — referenced by auto-scroll useEffect. */}
            <div ref={messagesEndRef} />
          </div>

          <div className="border-t border-neutral-200 dark:border-white/10 p-3 space-y-2">
            {staged.length > 0 && (
              <div className="space-y-1.5 max-h-[40vh] overflow-y-auto">
                {staged.map(a => (
                  <AttachmentChip
                    key={a.chart_id}
                    attachment={a}
                    onRemove={() => unstageAttachment(a.chart_id)}
                  />
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 border border-neutral-300 bg-white dark:border-white/15 dark:bg-black/40 p-2">
              <textarea
                ref={draftRef}
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={staged.length > 0 ? 'Ask about these charts…' : 'Ask about your team…'}
                rows={1}
                className="flex-1 bg-transparent text-sm text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 resize-none focus:outline-none leading-6 max-h-32 overflow-y-auto"
              />
              <button
                type="button"
                onClick={handleSend}
                disabled={!draft.trim() || isSending}
                className="self-end p-1.5 bg-neutral-900 text-white hover:bg-neutral-700 dark:bg-white/10 dark:hover:bg-white/20 dark:text-neutral-200 disabled:opacity-30"
                aria-label="Send"
              >
                <ArrowUp className="w-4 h-4" />
              </button>
            </div>
          </div>
        </aside>
      )}
    </>
  );
};

const MobileDrawer: React.FC<AnalyticsChatDrawerProps> = ({ teamId, days }) => {
  const { drawerOpen, setDrawerOpen, staged, unstageAttachment, clearStaged } = useAttachmentContext();
  const { sessionId, messages, isSending, send, startNewSession, switchToSession } = useChatSession({ teamId, days });
  const [draft, setDraft] = useState('');
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const draftRef = useAutoGrowTextarea(draft);

  // Auto-scroll to bottom on new messages or while generating, mirroring
  // the desktop behavior so the latest content stays visible.
  useEffect(() => {
    if (!drawerOpen) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length, isSending, drawerOpen]);

  const [sheetHeightVh, setSheetHeightVh] = useState<number>(MOBILE_SHEET_MAX_VH);
  const dragStartRef = useRef<{ startY: number; startVh: number } | null>(null);

  // Reset to full height whenever drawer opens.
  useEffect(() => {
    if (drawerOpen) setSheetHeightVh(MOBILE_SHEET_MAX_VH);
  }, [drawerOpen]);

  const onDragStart = (e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragStartRef.current = { startY: e.clientY, startVh: sheetHeightVh };
  };
  const onDragMove = (e: React.PointerEvent) => {
    if (!dragStartRef.current) return;
    const dy = e.clientY - dragStartRef.current.startY;
    const vh = window.innerHeight;
    const newHeight = Math.max(
      30,
      Math.min(MOBILE_SHEET_MAX_VH, dragStartRef.current.startVh - (dy / vh) * 100)
    );
    setSheetHeightVh(newHeight);
  };
  const onDragEnd = () => {
    if (!dragStartRef.current) return;
    dragStartRef.current = null;
    // Snap: below 35 → close; 35-60 → partial (40vh); above 60 → full (80vh).
    if (sheetHeightVh < 35) {
      setDrawerOpen(false);
      setSheetHeightVh(MOBILE_SHEET_MAX_VH); // reset for next open
    } else if (sheetHeightVh < 60) {
      setSheetHeightVh(40);
    } else {
      setSheetHeightVh(MOBILE_SHEET_MAX_VH);
    }
  };

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || isSending) return;
    setDraft('');
    const toSend = [...staged];
    clearStaged();
    await send(text, toSend);
  };

  return (
    <>
      {/* FAB — always visible on mobile */}
      <button
        type="button"
        onClick={() => setDrawerOpen(!drawerOpen)}
        aria-label={drawerOpen ? 'Close AI chat' : 'Open AI chat'}
        className="fixed z-40 bottom-4 right-4 flex items-center justify-center bg-white text-neutral-900 border border-neutral-300 dark:bg-neutral-900 dark:text-neutral-200 dark:border-white/20 shadow-lg"
        style={{ width: 52, height: 52 }}
      >
        <Sparkles className="w-5 h-5" />
      </button>

      {/* Bottom sheet */}
      {drawerOpen && (
        <>
          {/* Tap-to-close dim area above sheet */}
          {/* Visual dim only — pointer-events-none so taps pass through to
              chart cards behind the sheet (otherwise attach-mode breaks on
              mobile). Close affordances: drag handle pulled past 30vh, or
              the X in the header. */}
          <div
            className="fixed inset-0 z-30 bg-black/30 pointer-events-none"
            aria-hidden
          />
          <aside
            className="fixed left-0 right-0 bottom-0 z-40 bg-white border-t border-neutral-200 dark:bg-neutral-900 dark:border-white/15 flex flex-col transition-[height] duration-150"
            style={{ height: `${sheetHeightVh}vh` }}
            aria-label="AI chat"
          >
            {/* Drag handle */}
            <div
              onPointerDown={onDragStart}
              onPointerMove={onDragMove}
              onPointerUp={onDragEnd}
              onPointerCancel={onDragEnd}
              className="flex justify-center py-2 touch-none cursor-ns-resize"
            >
              <div className="w-10 h-1 bg-neutral-300 dark:bg-white/25" />
            </div>

            {/* Header (same semantics as desktop) */}
            <header className="flex items-center justify-between border-b border-neutral-200 dark:border-white/10 px-4 py-2">
              <div className="text-sm font-medium text-neutral-900 dark:text-neutral-200 truncate">
                {sessionId ? 'Chat' : 'New chat'}
              </div>
              <div className="flex items-center gap-2">
                <ChatHistoryDropdown
                  teamId={teamId}
                  currentSessionId={sessionId}
                  onSelect={switchToSession}
                />
                <button
                  type="button"
                  onClick={startNewSession}
                  className="text-xs text-neutral-500 dark:text-neutral-400 px-2 py-1"
                  aria-label="Start new chat"
                >
                  + New chat
                </button>
                <button
                  type="button"
                  onClick={() => setDrawerOpen(false)}
                  className="text-neutral-500 dark:text-neutral-400 p-1"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </header>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
              {messages.length === 0 && !isSending && (
                <div className="text-neutral-600 dark:text-neutral-400 text-sm">
                  Tap a chart behind this panel to attach it.
                </div>
              )}
              {messages.map(m => (
                <div key={m.id}>
                  {m.role === 'user' ? (
                    <div className="bg-neutral-100 border border-neutral-200 dark:bg-white/5 dark:border-white/10 px-3 py-2">
                      <div className="text-[11px] text-neutral-500 dark:text-neutral-400 mb-1">You</div>
                      <div className="text-sm text-neutral-900 dark:text-neutral-100 whitespace-pre-wrap">{m.content}</div>
                      {m.attachments && m.attachments.length > 0 && (
                        <div className="mt-2 space-y-1.5">
                          {m.attachments.map(a => <AttachmentChip key={a.chart_id} attachment={a} />)}
                        </div>
                      )}
                    </div>
                  ) : (
                    <MarkdownText compact>{m.content}</MarkdownText>
                  )}
                </div>
              ))}
              {isSending && <div className="text-sm text-neutral-500 italic">Thinking…</div>}
              {/* Scroll anchor — referenced by auto-scroll useEffect. */}
              <div ref={messagesEndRef} />
            </div>

            {/* Composer */}
            <div className="border-t border-neutral-200 dark:border-white/10 p-3 space-y-2">
              {staged.length > 0 && (
                <div className="space-y-1.5 max-h-[30vh] overflow-y-auto">
                  {staged.map(a => (
                    <AttachmentChip
                      key={a.chart_id}
                      attachment={a}
                      onRemove={() => unstageAttachment(a.chart_id)}
                    />
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 border border-neutral-300 bg-white dark:border-white/15 dark:bg-black/40 p-2">
                <textarea
                  ref={draftRef}
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  onKeyDown={e => {
                    // Mirror desktop: Enter sends, Shift+Enter inserts a newline.
                    // Touch users typically tap the send button; this handler
                    // primarily serves tablet + external-keyboard cases.
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder={staged.length > 0 ? 'Ask about these charts…' : 'Ask about your team…'}
                  rows={1}
                  className="flex-1 bg-transparent text-sm text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 resize-none focus:outline-none leading-6 max-h-32 overflow-y-auto"
                />
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={!draft.trim() || isSending}
                  className="self-end p-2 bg-neutral-900 text-white dark:bg-white/10 dark:text-neutral-200 disabled:opacity-30"
                  aria-label="Send"
                >
                  <ArrowUp className="w-4 h-4" />
                </button>
              </div>
            </div>
          </aside>
        </>
      )}
    </>
  );
};

export { DRAWER_WIDTH_PX };
